import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  // wb-plain: the kit styles itself completely (padding, height, contained
  // hover) — opt out of index.css's global button margin + hover lift/ring,
  // which stacked on top and clipped inside portaled dialog content.
  "wb-plain inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-color)] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          // The Workbench's own solid buttons (Calculate, .wb-tbtn--solid) are
          // --wb-accent-solid on --wb-accent-ink, which is the pair that was
          // contrast-checked in all three themes; --primary-color/white was
          // not (2.14:1 on dark's #38bdf8). Same fill everywhere, so a dialog's
          // primary button matches Calculate. The old pair stays as a fallback
          // for any surface that has not defined the accent tokens.
          "bg-[var(--wb-accent-solid,var(--primary-color))] text-[var(--wb-accent-ink,var(--button-text-color))] hover:brightness-95",
        secondary:
          // Border keeps the button visible where --secondary-color sits close
          // to the surrounding card surface (dark/island themes).
          "border border-[var(--border-color)] bg-[var(--secondary-color)] text-[var(--text-color)] hover:brightness-95",
        outline:
          "border border-[var(--border-color)] bg-transparent text-[var(--text-color)] hover:bg-[var(--hover-bg-color)]",
        ghost:
          "bg-transparent text-[var(--text-color)] hover:bg-[var(--hover-bg-color)]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
